import React, {useCallback, useMemo, useState} from "react";
import {Menu, MenuItem} from "@mui/material";
import {grey} from "@mui/material/colors";
import {css} from "@emotion/css";
import {DefaultTabPage, defaultTabKey} from "./keys";
import {useUserPrefString} from "./useUserPref";
import {useUserPreferences} from "./UserPreferencesProvider";

// The hint is hidden until the tab row is hovered. TabWrapper applies this rule;
// other hosts add `defaultTabHostClass` to the element that holds the tabs.
export const defaultTabHintClass = css({
    opacity: 0,
    transition: "opacity 0.15s",
    marginLeft: "auto",
    alignSelf: "center",
    paddingLeft: 8,
    paddingRight: 8,
    fontSize: "0.7rem",
    color: grey["600"],
    whiteSpace: "nowrap",
    userSelect: "none",
});
export const defaultTabHostClass = css({
    [`&:hover .${defaultTabHintClass}`]: {
        opacity: 1,
    },
});

type ContextState = {
    top: number;
    left: number;
    value: string;
}

export type DefaultTabControls = {
    // stored preference, or `fallback` when none is stored or the stored value is not allowed
    defaultValue: string;
    stored: string | null;
    isDefault(value: string): boolean;
    // spread onto each <Tab>: {...dt.tabProps("doors")}
    tabProps(value: string): { onContextMenu(e: React.MouseEvent): void };
    // render once beside the <Tabs>, inside its flex row: a right-aligned hint plus the context menu
    controls: React.ReactNode;
}

// Lets the user right-click a tab and choose "Set as default tab" for a page.
// Use useTabQueryParam when the tab lives in the query string (most pages).
// For a path-based tab, see Logistics.tsx: write the default into the path once
// when the path omits the tab, so the URL means the same for everyone.
//
//   const dt = useDefaultTab({page: "shopdrawing.logistics", fallback: "frame-hm"});
//   <Tab value="frame-hm" label="HM Frame" {...dt.tabProps("frame-hm")} />
//   {dt.controls}
export function useDefaultTab(props: {
    page: DefaultTabPage;
    fallback: string;
    allowed?: string[];
}): DefaultTabControls {
    const key = defaultTabKey(props.page);
    const [stored, setStored] = useUserPrefString(key, "");
    const ctx = useUserPreferences();
    const [context, setContext] = useState<ContextState | null>(null);

    const allowedKey = props.allowed ? props.allowed.join("\u0000") : null;
    const defaultValue = useMemo(() => {
        if (stored === "") return props.fallback;
        if (allowedKey !== null && allowedKey.split("\u0000").indexOf(stored) === -1) return props.fallback;
        return stored;
    }, [stored, allowedKey, props.fallback]);

    const tabProps = useCallback((value: string) => ({
        onContextMenu: (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setContext({top: e.clientY, left: e.clientX, value});
        },
    }), []);

    const isDefault = useCallback((value: string) => stored === value, [stored]);

    const menu = context ? (
        <Menu open onClose={() => setContext(null)}
              anchorReference="anchorPosition"
              anchorPosition={{top: context.top, left: context.left}}>
            <MenuItem disabled={stored === context.value} onClick={() => {
                setStored(context.value);
                setContext(null);
            }}>
                {stored === context.value ? "Default tab" : "Set as default"}
            </MenuItem>
            {stored !== "" && <MenuItem onClick={() => {
                ctx.clear(key);
                setContext(null);
            }}>
                Clear default tab
            </MenuItem>}
        </Menu>
    ) : null;

    const controls = (
        <>
            <div className={defaultTabHintClass}>
                Right-click a tab to make it the default
            </div>
            {menu}
        </>
    );

    return {
        defaultValue,
        stored: stored === "" ? null : stored,
        isDefault,
        tabProps,
        controls,
    };
}
